import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import classNames from "classnames";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./style";


interface OwnProps {
	dirty: boolean;
	saving: boolean;
	authError?: string | null;
	/** Shows a transient confirmation after a save resolves. Cleared by the host. */
	saveSuccess?: boolean;
	/** Set when a save throws, so a failure is never silent. */
	saveError?: string | null;
	onSave: () => void;
	onCancel: () => void;
	onSaveFeedbackClose?: () => void;
	/** Span the viewport rather than the content column — for pages that cap their width. */
	fullBleed?: boolean;
	/**
	 * The MAX-4419 design kit's bar: Large buttons, and pinned to the bottom of the window on a page
	 * shorter than it. Off by default, so pages outside the refresh keep today's bar.
	 */
	refreshed?: boolean;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

const Component: React.FC<Props> = ({
	classes = {},
	dirty,
	saving,
	authError,
	saveSuccess,
	saveError,
	onSave,
	onCancel,
	onSaveFeedbackClose,
	fullBleed,
	refreshed = false,
}) => {
	return (
		<>
			<Mui.Box className={classes.spacer} />
			<Mui.Box className={classNames(classes.root, fullBleed && classes.fullBleed)}>
				{saveError && (
					<Mui.Box display="flex" alignItems="center" style={{ marginRight: "auto" }}>
						<Icons.AlertTriangle size={16} style={{ color: "#f44336", marginRight: 8 }} />
						<Mui.Typography variant="body2" style={{ color: "#f44336" }}>{saveError}</Mui.Typography>
					</Mui.Box>
				)}
				{dirty && !authError && (
					<Mui.Box display="flex" alignItems="center">
						<Icons.AlertTriangle size={16} style={{ color: "#F59E0B", marginRight: 8 }} />
						<Mui.Typography variant="body2" style={{ color: "#5F6368" }}>You have unsaved changes!</Mui.Typography>
					</Mui.Box>
				)}
				{authError && (
					<Mui.Box display="flex" alignItems="center" style={{ marginRight: "auto" }}>
						<Icons.AlertTriangle size={16} style={{ color: "#f44336", marginRight: 8 }} />
						<Mui.Typography variant="body2" style={{ color: "#f44336" }}>Please review errors above before saving changes.</Mui.Typography>
					</Mui.Box>
				)}
				<Mui.Button
					variant="outlined"
					color="primary"
					size={refreshed ? "large" : undefined}
					className={classNames(refreshed && classes.large, refreshed && classes.outlined)}
					onClick={onCancel}
				>
					Cancel
				</Mui.Button>
				<Mui.Button
					variant="contained"
					color="primary"
					size={refreshed ? "large" : undefined}
					className={classNames(refreshed && classes.large)}
					disabled={saving}
					onClick={onSave}
				>
					{saving ? "Saving..." : "Save Changes"}
				</Mui.Button>
				<Mui.Snackbar
					open={Boolean(saveSuccess) || Boolean(saveError)}
					autoHideDuration={4000}
					onClose={onSaveFeedbackClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Mui.Alert severity={saveError ? "error" : "success"} onClose={onSaveFeedbackClose}>
						{saveError || "Changes saved."}
					</Mui.Alert>
				</Mui.Snackbar>
			</Mui.Box>
		</>
	);
};

export const SettingsStickyFooter = withStyles(Component, styles);
